/**
 * Main App component with TanStack React Query setup and routing
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AppAuthProvider } from './components/auth/AuthProvider'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary'
import { AppLayout } from './components/layout/AppLayout'
import { ThemeProvider } from './components/theme/ThemeProvider'
import { Toaster } from './components/ui/sonner'
import { initPerformanceMonitoring } from './utils/performance'
import { useNetworkStatus } from './hooks/useNetworkStatus'
import { useServiceWorker } from './hooks/useServiceWorker'
import { InstallPrompt } from './components/pwa/InstallPrompt'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Assets from './pages/Assets'
import PriceHistory from './pages/PriceHistory'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Expectations from './pages/Expectations'
import './index.css'

// Initialize performance monitoring
initPerformanceMonitoring()

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

function App() {
  // Monitor network status and update store
  useNetworkStatus()

  // Register service worker
  useServiceWorker()

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="gold-tracker-ui-theme">
        <AppAuthProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route
                path="/login"
                element={
                  <RouteErrorBoundary routeName="Login">
                    <Login />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="Dashboard">
                        <Dashboard />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assets"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="Assets">
                        <Assets />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales"
                element={<Navigate to="/assets" replace />}
              />
              <Route
                path="/prices"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="PriceHistory">
                        <PriceHistory />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="Reports">
                        <Reports />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="Settings">
                        <Settings />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expectations"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <RouteErrorBoundary routeName="Expectations">
                        <Expectations />
                      </RouteErrorBoundary>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </BrowserRouter>
            <Toaster />
            <InstallPrompt />
          </QueryClientProvider>
        </AppAuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
